import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth } from 'aws-amplify';
import { ConstantsService } from '../services/constants.service';
import { DataService } from '../services/data.service';
import { environment } from '../../environments/environment';

interface SignupFlowResponse {
  success: boolean;
  platform: 'web' | 'mobile' | 'unknown';
  action: 'app_redirect' | 'store_redirect' | 'none' | 'unsupported' | 'error';
  error?: string;
}
@Component({
  selector: 'app-verify',
  templateUrl: './verify.component.html',
  styleUrls: ['./verify.component.scss']
})
export class VerifyComponent implements OnInit {
  public isError = false;
  public isUnknownError = false;
  public errorMessage;
  public loginForm: UntypedFormGroup;
  public isProcessing = false;
  fromsubscription: any;
  public isLoading = false;
  public login = true;
  firstTimeUser: string;
  firstTimeUserSub: string;
  isSuccess: boolean = false;
  errorMessageSub: string;
  errorString : any = null;
  errorCopied : boolean = false;
  isSpinning : boolean = true;
  isAppSignup: boolean = false;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public constants: ConstantsService,
    private fb: UntypedFormBuilder,
    private dataService: DataService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      let username = this.dataService.decodeURIComponentSpecial(params.username);
      let code = params.code;
      let appclientId = params.clientId;
      if (appclientId && appclientId === environment.USERPOLL_WEBCLIENT) {
        this.handleWebSignup(username, code);
      } else {
        this.isAppSignup = true;
        this.handleAppSignup(username, code);
      }
    })
  }

  async handleWebSignup(username: string, code: string): Promise<void> {
    try {
      this.isSpinning = false;
      this.confirmSignUp(username, code);
      this.initialiseForm();
    } catch (error) {
      console.error('Error in web signup:', error);
      throw error;
    }
  }

  async handleAppSignup(username, code) {
    try{
      const isWebUserAgent = await this.isDesktopUserAgent();
      if (isWebUserAgent) {
        this.isSpinning = false;
        this.confirmSignUp(username, code);
        return { success: true, platform: 'web', action: 'none' };
      } else {
        try {
          await this.confirmSignUp(username, code);
        } catch (error) {
          console.error('Error in app signup:', error);
          throw error;
        }
        this.isSpinning = false;
        const userAgent = navigator.userAgent.toLowerCase() || navigator.vendor.toLowerCase();
        const isIOS = /iphone|ipad|ipod/i.test(userAgent);
        const isAndroid = /android/i.test(userAgent);
        
        let storeUrl = '';
        if (isIOS) {
          storeUrl = environment.WINDOWSWAP_APP.APPLE_STORE_URL_PREFIX;
        } else if (isAndroid) {
          storeUrl = `${environment.WINDOWSWAP_APP.PLAYSTORE_URL_PREFIX}${environment.WINDOWSWAP_APP.APPLICATION_ID}`;
        } else {
          throw new Error('Unsupported mobile platform');
        }
        return await this.handleAppRedirect(storeUrl, isIOS ? 'ios' : 'android');
      }
    }catch (error) {
      console.log('Error in App signup');
      return { success: false, platform: 'mobile', action: 'unsupported' };
    }
  }

  async handleAppRedirect(storeUrl: string, platform: string): Promise<SignupFlowResponse> {
    try {
      if (platform === 'android') {
        // Android-specific deep link with Play Store fallback
        window.location.href = `intent://login-form#Intent;scheme=${environment.WINDOWSWAP_APP.DEEP_LINK_URL};package=${environment.WINDOWSWAP_APP.APPLICATION_ID};S.browser_fallback_url=${encodeURIComponent(storeUrl)};end;`;
        return { success: true, platform: 'mobile', action: 'app_redirect' };
      } else {
        // iOS-specific handling with visibility check
        window.location.href = `${environment.WINDOWSWAP_APP.DEEP_LINK_URL}://login-form`;

        return new Promise((resolve) => {
          const storeRedirectTimeout = 500; // Shorter timeout for iOS
          let timeoutId: NodeJS.Timeout;

          // Success handler - called if app opens
          const visibilityHandler = () => {
            if (document.hidden) {
              clearTimeout(timeoutId);
              resolve({ success: true, platform: 'mobile', action: 'app_redirect' });
            }
          };

          // Set up visibility change listener
          document.addEventListener('visibilitychange', visibilityHandler);

          // Set up fallback timeout
          timeoutId = setTimeout(() => {
            document.removeEventListener('visibilitychange', visibilityHandler);
            window.location.href = storeUrl;
            resolve({ success: true, platform: 'mobile', action: 'store_redirect' });
          }, storeRedirectTimeout);
        });
      }
    } catch (error) {
      console.error('Error redirecting to mobile app:', error);
      window.location.href = storeUrl;
      return { success: true, platform: 'mobile', action: 'store_redirect' };
    }
  }

  async isDesktopUserAgent(): Promise<boolean> {
    return new Promise((resolve) => {
      const userAgent = navigator.userAgent.toLowerCase() || navigator.vendor.toLowerCase();
      const isMobile = /android|iphone|ipad|ipod/i.test(userAgent);
      resolve(!isMobile && !this.isIpadInDesktopMode());
    })
  }

  isIpadInDesktopMode(): boolean {
    const userAgent = navigator.userAgent.toLowerCase() || navigator.vendor.toLowerCase();
    // Detect iPads that spoof macOS
    if (/macintosh/.test(userAgent)) {
      if (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) {
        return true; // iPad in desktop mode
      }
    }
    return false;
  }


  async confirmSignUp(username, code) {
    try {
      await Auth.confirmSignUp(username, code);
      // this.router.navigate(['/login'], { replaceUrl: true });
      this.isSuccess = true;
      this.isSpinning = false;
      if(this.isAppSignup) {
        this.firstTimeUser = this.constants.FIRST_TIME_VERIFIED_TEXT;
        this.firstTimeUserSub = this.constants.FIRST_TIME_APP_VERIFIED_SUB_TEXT;
      } else {
        this.firstTimeUser = this.constants.FIRST_TIME_VERIFIED_TEXT;
        this.firstTimeUserSub = this.constants.FIRST_TIME_WEB_VERIFIED_SUB_TEXT;
      }
    } catch (error) {
      this.isError = true
      this.isSpinning = false;
      if (error.code == this.constants.USER_CONFIRM_ERROR.CODE && error.message == this.constants.USER_CONFIRM_ERROR.MESSAGE) {
        if(this.isAppSignup) {
          this.errorMessage = this.constants.ERROR_ALREADY_VERIFIED_TEXT;
          this.errorMessageSub = this.constants.ERROR_ALREADY_VERIFIED_APP_SUB_TEXT;
        } else {
          this.errorMessage = this.constants.ERROR_ALREADY_VERIFIED_TEXT;
          this.errorMessageSub = this.constants.ERROR_ALREADY_VERIFIED_WEB_SUB_TEXT;
        }
      } else {
        this.isUnknownError = true;
        this.errorMessage = this.constants.ERROR_UNKNOWN_VERIFICATION_TEXT;
        this.errorMessageSub = this.constants.ERROR_UNKNOWN_VERIFICATION_SUB_TEXT;
        this.errorString = btoa(JSON.stringify(error));
        console.log('error :', this.errorString);
        throw error;
      }
    }
  }

  initialiseForm = () => {
    this.loginForm = this.fb.group({
      email: new UntypedFormControl('', [Validators.email, Validators.required]),
      password: new UntypedFormControl('', [
        Validators.required
      ])
    })
  }
  async onClicklogin() {
    this.isProcessing = true;
    let username = this.loginForm.value.email;
    let password = this.loginForm.value.password;
    try {
      const user = await Auth.signIn(username, password);
      this.fromsubscription === 'true' ? this.router.navigate(['payment'], { replaceUrl: true }) : this.router.navigate(['/'], { replaceUrl: true });
    } catch (error) {
      if (error.message === "User is not confirmed.") {
        document.getElementById("resend").style.display = `block`
      }
      else {
        if (error.message === "Incorrect username or password.") {
          document.getElementById("errorAlert").innerHTML = 'Your email or password don’t match. Try again'
        }
        else {
          document.getElementById("errorAlert").innerHTML = error.message
        }
      }

      this.isProcessing = false;
    }
  }
  async onClickResend() {
    this.isLoading = true
    let username = this.loginForm.value.email
    try {
      await Auth.resendSignUp(username);
      document.getElementById("resend").innerHTML = 'Success, Please click the confirmation link send to your email'
      this.isLoading = false;
    } catch (err) {
      document.getElementById("resend").innerHTML = err.message
      this.isLoading = false;
    }
  }
  onClickSowPassowrd() {
    let inputFiled = (<HTMLInputElement>document.getElementById('input-password'))
    if (inputFiled.type === 'password') {
      inputFiled.type = 'text'
    }
    else {
      inputFiled.type = 'password'
    }
  }

  copyText() {
    navigator.clipboard.writeText('error-'+this.errorString).then(function () {
    });
    this.errorCopied = true;
  }
}
